import React, { useMemo } from 'react';
import { Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { Check1Row, Check2Row, QueryResponse } from '../../../services/pwj310Api';

interface ResultTableProps {
  result: QueryResponse;
}

const fmtNum = (v: number | null): string => {
  if (v == null) return '';
  return v.toFixed(1);
};

const check1Columns: ColumnsType<Check1Row & { _key: string }> = [
  {
    title: '原料代號',
    dataIndex: 'f111_01',
    key: 'f111_01',
    width: 90,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '原料名稱',
    dataIndex: 'f111_11',
    key: 'f111_11',
    width: 150,
    ellipsis: true,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '原料規格',
    dataIndex: 'f111_21',
    key: 'f111_21',
    width: 150,
    ellipsis: true,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '可用幅寬',
    dataIndex: 'f111_22',
    key: 'f111_22',
    width: 80,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '全幅幅寬',
    dataIndex: 'f111_221',
    key: 'f111_221',
    width: 80,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '幅寬單位',
    dataIndex: 'f111_23',
    key: 'f111_23',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '碼重',
    dataIndex: 'f111_24',
    key: 'f111_24',
    width: 70,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '米平方重',
    dataIndex: 'f111_241',
    key: 'f111_241',
    width: 80,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '說明',
    dataIndex: 'f111_12',
    key: 'f111_12',
    width: 150,
    ellipsis: true,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '工廠代號-布號',
    dataIndex: 'f111_26',
    key: 'f111_26',
    width: 200,
    render: (v: string) => {
      if (!v) return '';
      const lines = v.split('\n').filter(Boolean);
      return (
        <div style={{ whiteSpace: 'pre-line', lineHeight: '1.4' }}>
          {lines.map((line, i) => (
            <div key={i}>{line.trim()}</div>
          ))}
        </div>
      );
    },
  },
];

const check2Columns: ColumnsType<Check2Row & { _key: string }> = [
  {
    title: '工廠代號-布號',
    dataIndex: 'f111_26',
    key: 'f111_26',
    width: 160,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '原料代號',
    dataIndex: 'f111_01',
    key: 'f111_01',
    width: 90,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '原料名稱',
    dataIndex: 'f111_11',
    key: 'f111_11',
    width: 150,
    ellipsis: true,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '原料規格',
    dataIndex: 'f111_21',
    key: 'f111_21',
    width: 150,
    ellipsis: true,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '可用幅寬',
    dataIndex: 'f111_22',
    key: 'f111_22',
    width: 80,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '全幅幅寬',
    dataIndex: 'f111_221',
    key: 'f111_221',
    width: 80,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '幅寬單位',
    dataIndex: 'f111_23',
    key: 'f111_23',
    width: 80,
    render: (v: string) => (v || '').trim(),
  },
  {
    title: '碼重',
    dataIndex: 'f111_24',
    key: 'f111_24',
    width: 70,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '米平方重',
    dataIndex: 'f111_241',
    key: 'f111_241',
    width: 80,
    align: 'right' as const,
    render: fmtNum,
  },
  {
    title: '說明',
    dataIndex: 'f111_12',
    key: 'f111_12',
    width: 150,
    ellipsis: true,
    render: (v: string) => (v || '').trim(),
  },
];

const ResultTable: React.FC<ResultTableProps> = ({ result }) => {
  const check1Data = useMemo(
    () =>
      (result.check1Data || []).map((row, i) => ({
        ...row,
        _key: `c1-${row.f111_01}-${i}`,
      })),
    [result.check1Data]
  );

  const check2Data = useMemo(
    () =>
      (result.check2Data || []).map((row, i) => ({
        ...row,
        _key: `c2-${row.f111_01}-${row.f111_26}-${i}`,
      })),
    [result.check2Data]
  );

  const hasCheck1 = check1Data.length > 0;
  const hasCheck2 = check2Data.length > 0;

  // If only one type of result, show a single table without tabs
  if (hasCheck1 && !hasCheck2) {
    return (
      <Table
        columns={check1Columns}
        dataSource={check1Data}
        rowKey="_key"
        size="small"
        scroll={{ x: 1200, y: 500 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
      />
    );
  }

  if (hasCheck2 && !hasCheck1) {
    return (
      <Table
        columns={check2Columns}
        dataSource={check2Data}
        rowKey="_key"
        size="small"
        scroll={{ x: 1100, y: 500 }}
        pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
      />
    );
  }

  // Both results present — use Tabs
  const items = [
    {
      key: 'check1',
      label: `原料代號別 (${check1Data.length})`,
      children: (
        <Table
          columns={check1Columns}
          dataSource={check1Data}
          rowKey="_key"
          size="small"
          scroll={{ x: 1200, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
    {
      key: 'check2',
      label: `工廠布號別 (${check2Data.length})`,
      children: (
        <Table
          columns={check2Columns}
          dataSource={check2Data}
          rowKey="_key"
          size="small"
          scroll={{ x: 1100, y: 500 }}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100'] }}
        />
      ),
    },
  ];

  return <Tabs items={items} />;
};

export default ResultTable;
